import TrackingRepository from "src/shared/TrackingRepository";
import sendMessage from "src/shared/sendMessage";
import { Workflows } from "src/shared/workflows";
import { EventService, Events } from "src/shared/EventService";
import { logError } from "src/shared/logError";

export const DURATIONS = {
    sixMonths: 15724800,
    threeMonths: 7862400,
    oneMonth: 2592000,
    oneDay: 86400,
    twoWeeks: 1209600,
    oneHour: 3600,
};

type WorkflowRateMap = {
  [key: string]: number;
};

class VibesTargeter {
    private readonly repo: TrackingRepository;
    private readonly rateMap: WorkflowRateMap;
    private readonly eventService: EventService;
    private readonly platformWorkflow: string;
    private readonly platform: string;

    constructor(
        repo: TrackingRepository,
        eventService: EventService,
        rateMap: WorkflowRateMap,
        platformWorkflow: string,
        platform = '',
    ) {
        this.repo = repo;
        this.eventService = eventService;
        this.rateMap = rateMap;
        this.platformWorkflow = platformWorkflow;
        this.platform = platform;
    }

    private customerIsEligible(): boolean {
        // set dismiss durations to 3mo/6mo for SC and two weeks for VC
        let dismissDuration = DURATIONS.threeMonths;
        let submitDuration = DURATIONS.sixMonths;

        if (this.platform.toLocaleLowerCase() === 'vendorcentral') {
            dismissDuration = DURATIONS.twoWeeks;
            submitDuration = DURATIONS.oneMonth;
        }

        // If the user has dismissed or submitted then they are not eligible
        return !(this.repo.hasDismissedWithin(dismissDuration) || this.repo.hasSubmittedWithin(submitDuration));
    }

    private sampleEligibility(isEligible: boolean) {
        if (this.shouldRandomlyTarget(1.5 / 100)) {
            const event = isEligible ? Events.eligible : Events.notEligible;
            this.eventService.saveEvent(event, 'NO_WORKFLOW').catch((e) => {
                logError(e, 'Unable to save eligibility event');
            });
        }
    }

    public shouldTarget(doRandomCheck = true): [string | false, boolean] {
        const isEligible = this.customerIsEligible();

        this.sampleEligibility(isEligible);

        if (!this.customerIsEligible()) {
            return [false, false];
        }

        // If ask me later was chosen, determine if enough time has passed and
        // return that workflow, or continue to wait
        const aml = this.repo.getAskMeLater();
        if (aml) {
            if (Date.now() >= aml.time) {
                this.repo.customerWasTargeted(aml.workflow);

                const workflow = aml.workflow.startsWith("vibes:") ? aml.workflow.substr(6) : aml.workflow;
                return [workflow, true];
            }

            return [false, false];
        }

        // initial random check, should we continue for this user?
        if (doRandomCheck && !this.shouldRandomlyTarget(1 / 100)) {
            return [false, false];
        }

        // Get workflows and display question
        const foundWorkflows = this.repo.getWorkflowList();

        if (this.platformWorkflow) {
            foundWorkflows.push(this.platformWorkflow);
        }

        // If the workflow list is empty, return false
        if (!foundWorkflows.length) {
            return [false, false];
        }

        const workflows: string[] = [];

        // Check the rate map and pad the workflow array based on the rate map entry
        for (const workflow of foundWorkflows) {
            // Every workflow gets at least one entry, even if it's weighted at a decimal
            const count = Math.ceil(this.rateMap[workflow] ?? 1);
            const items = new Array(count).fill(workflow);
            workflows.push(...items);
        }

        // Return a random workflow
        const wfToTarget = workflows[Math.floor(Math.random() * workflows.length)];

        // See if we should randomly target this user based on the workflow weight to avoid oversampling
        // This second check allows us to adjust the overall randomness without digging into targeting eligibility on every request
        if (wfToTarget in this.rateMap && doRandomCheck && this.rateMap[wfToTarget] < 1 && !this.shouldRandomlyTarget(this.rateMap[wfToTarget])) {
            return [false, false];
        }

        this.repo.customerWasTargeted(wfToTarget);

        if (!this.isWorkflowLive(wfToTarget)) {
          return [false, false];
        }

        return [wfToTarget, false];
    }

    public targetParent(window: Window, workflow: string, title: string, askMeLater = false) {
        this.repo.customerWasTargeted(workflow, '', askMeLater);
        sendMessage(window, 'vibes-open', [title]);
    }

    private shouldRandomlyTarget(targetRate: number): boolean {
        const override = this.repo.getRateOverride();
        const rate = override || targetRate;

        return Math.random() < rate;
    }

    /**
     * This function acts as host-side check to see if a workflow is live before launching
     * so that the survey and tracking can be developed independently from launch
     *
     * @param workflow
     */
    private isWorkflowLive(workflow: string): boolean {
        const workflows = new Set<string>([
            Workflows.chargebacks,
            Workflows.compliance,
            Workflows.fees,
            Workflows.inventory,
            Workflows.listings,
            Workflows.listingAssets,
            Workflows.listingQuality,
            Workflows.manageReviews,
            Workflows.performance,
            Workflows.pricing,
            Workflows.respondToCustomerMessages,
            Workflows.salesCurrent,
            Workflows.salesTraffic,
            Workflows.shipping,
            Workflows.support,
            Workflows.fixInventory,
            Workflows.purchaseOrders,
            Workflows.payment,
            Workflows.brand,
            Workflows.sellingApplications,
            Workflows.sellerCentralPlatform,
            Workflows.vendorCentralPlatform,
        ]);

        return workflows.has(workflow);
    }
}

export default VibesTargeter;
